//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import * as bulkActionsApi from '@apis/bulkActionsApi';
import * as galleriesApi from '@apis/galleries/galleriesApi';
import * as shareApi from '@share/api/shareApi';
import { galleriesSourceFilterOptionToShareFilterOption } from '@galleries/utils/bazaarvoiceNetworkUtils';
import { getIsBazaarvoiceNetworkGallerySelected } from '@galleries/selectors/getIsBazaarvoiceNetworkGallerySelected';
import { IGallery, ISite } from '@apis/galleries/galleriesApiModels';
import { pinnedMediaToolbarActions } from '@galleries/actions/pinnedMediaToolbarActions';
import { previewGalleryModalActions } from '@galleries/actions/previewGalleryModalActions';
import {
  FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID,
  GALLERIES_TOAST_TYPES,
} from '@galleries/constants/galleriesConstants';
import { IAutoApprovalConfiguration } from '@galleries/models/AutoApprovalConfiguration';
import {
  GALLERY_ITEM_STATUS_VALUES,
  IGalleriesData,
  IGalleryItem,
  InstaShopAutoApprovalSources,
  SOURCE_FILTER_VALUES,
} from '@galleries/models/galleriesStateModel';
import { PinnedMediaToolbarContext } from '@galleries/models/pinnedMediaToolbarModel';
import { getAllAlphabetizedGalleries } from '@galleries/selectors/getAllAlphabetizedGalleries';
import {
  GalleriesClientType,
  getGalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import { getPinnedMediaPositionMap } from '@galleries/selectors/getPinnedMediaPositionMap';
import { getSelectedGallery } from '@galleries/selectors/getSelectedGallery';
import { isInstaShopGallery } from '@galleries/utils/isInstaShopGallery';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import ReelId from '@models/ReelId';
import SiteName from '@models/SiteName';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { createThunk } from '@redux/createThunk';
import { SliceNames } from '@redux/SliceNames';
import normalize, { Normalized } from '@utils/normalize';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { crl8 } from 'app/curalate.globals';
import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import gaApi from 'app/initialization/ga/ga-api';
import { autoApprovalModalActions } from './autoApprovalModalSlice';
import { getStringFormatDateRangeValue } from '@components/date-range-picker/dateRangePickerModels';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';
import { IGetBazaarvoiceNetworkSharedMediaRequestParams } from '@share/api/shareApiModel';

export const initialState: IGalleriesData = {
  galleries: Normalized.empty(),
  sites: [],
  flags: {
    isLoadingInitialGalleryData: true,
    isFetching: false,
    isFetchingByCuid: false,
    isFetchingFiltered: false,
    isFetchingNext: false,
    isLastFetch: false,
    isFetchingGalleriesList: false,
    isFetchingSitesList: false,
    isFetchingAutoApprovalConfigs: false,
    isFetchingRightsNeededCounts: false,
    isSkippingRightsOnItems: PojoSet.empty<ReelId>(),
  },
  items: {
    byId: {},
    orderedIds: [],
    totalItemCount: 0,
    pinnedItemsOrderedIds: [],
  },
  siteName: undefined,
  currentlyVisibleToastTypes: {},
  rightsNeededItemCounts: {},
};

const { reducer, actions, caseReducers } = createSlice({
  name: SliceNames.GalleriesData,
  initialState,
  reducers: {
    requestGalleriesData: (state) => {
      state.flags.isFetchingGalleriesList = true;
    },

    // Note: This will NOT WORK if you intend to add incremental metadata (which may be desired in the future)
    // If that's needed, you will need to append the byId and orderedId data rather than overwriting it
    receiveGalleriesDataSuccess: (
      state,
      action: PayloadAction<{ siteName: SiteName; galleries: IGallery[] }>,
    ) => {
      const { galleries, siteName } = action.payload;
      state.galleries = normalize(galleries, (gallery) => gallery.containerId);

      state.flags.isFetchingGalleriesList = false;
      state.siteName = siteName;
    },

    // Used to update properties for a single gallery
    receiveGallery: (state, action: PayloadAction<IGallery>) => {
      state.galleries.byId = PojoMap.set(
        state.galleries.byId,
        action.payload.containerId,
        action.payload,
      );
    },

    requestSiteData: (state) => {
      state.flags.isFetchingSitesList = true;
    },

    receiveSitesDataSuccess: (state, action: PayloadAction<ISite[]>) => {
      state.flags.isFetchingSitesList = false;
      state.sites = action.payload;
    },

    requestFilteredGalleryItems: (state) => {
      state.flags.isFetchingFiltered = true;
      state.flags.isLastFetch = false;
    },

    requestItemByCuid: (state) => {
      state.flags.isFetchingByCuid = true;
    },

    requestGalleryItems: (state) => {
      state.flags.isFetching = true;
      state.flags.isLastFetch = false;
    },

    requestNextGalleryItems: (state) => {
      state.flags.isFetchingNext = true;
    },

    receiveFilteredGalleryItemsSucceeded: (
      state,
      action: PayloadAction<{ items: IGalleryItem[]; totalItemCount: number }>,
    ) => {
      const { items, totalItemCount } = action.payload;
      const { byId, orderedIds } = normalize(
        items,
        (item) => item.networkItem.id,
      );

      state.flags.isFetchingFiltered = false;
      state.items.byId = {
        ...state.items.byId,
        ...byId,
      };
      state.items.orderedIds = orderedIds;
      state.items.totalItemCount = totalItemCount;
    },

    receiveItemByCuidSucceeded: (
      state,
      action: PayloadAction<IGalleryItem>,
    ) => {
      const { id } = action.payload.networkItem;
      const wasExistingItem = state.items.orderedIds.includes(id);

      state.flags.isFetchingByCuid = false;
      state.items.byId = {
        ...state.items.byId,
        [id]: action.payload,
      };
      state.items.orderedIds = wasExistingItem
        ? state.items.orderedIds
        : [id, ...state.items.orderedIds];
    },

    receiveGalleryItemsSucceeded: (
      state,
      action: PayloadAction<{ items: IGalleryItem[]; totalItemCount: number }>,
    ) => {
      const { items, totalItemCount } = action.payload;
      const { byId, orderedIds } = normalize(
        items,
        (item) => item.networkItem.id,
      );

      state.flags.isFetching = false;
      state.items.byId = {
        ...state.items.byId,
        ...byId,
      };
      state.items.orderedIds = orderedIds;
      state.items.totalItemCount = totalItemCount;
    },

    receiveNextGalleryItemsSucceeded: (
      state,
      action: PayloadAction<IGalleryItem[]>,
    ) => {
      const { byId, orderedIds } = normalize(
        action.payload,
        (item) => item.networkItem.id,
      );

      state.flags.isFetchingNext = false;
      state.items.byId = {
        ...state.items.byId,
        ...byId,
      };
      state.items.orderedIds = [...state.items.orderedIds, ...orderedIds];
    },

    receiveFilteredGalleryItemsFailed: (state) => {
      state.flags.isFetchingFiltered = false;
      state.flags.isLastFetch = true;
    },

    receiveGalleryItemsFailed: (state) => {
      state.flags.isFetching = false;
      state.flags.isLastFetch = true;
    },

    receiveNextGalleryItemsFailed: (state) => {
      state.flags.isFetchingNext = false;
    },

    removeGalleryItems: (state, action: PayloadAction<ItemId[]>) => {
      state.items = {
        ...state.items,
        ...Normalized.removeItems(state.items, action.payload),
      };
    },

    resetGalleryItems: (state) => {
      state.flags = {
        ...initialState.flags,
        // The isLoadingInitialGalleryData flag should not be reset as it indicates
        // all of the data necessary to render the page's shell has loaded
        isLoadingInitialGalleryData: state.flags.isLoadingInitialGalleryData,
      };
      state.items.byId = {};
      state.items.orderedIds = [];
      state.items.pinnedItemsOrderedIds = [];
      state.items.totalItemCount = 0;
    },

    setGalleryItemsLastFetch: (state) => {
      state.flags.isLastFetch = true;
    },

    setGalleryDataLoaded: (state) => {
      state.flags.isLoadingInitialGalleryData = false;
    },

    pinItemAction: (
      state,
      action: PayloadAction<{ cuid: Cuid; galleryItem: IGalleryItem }>,
    ) => {
      const { cuid, galleryItem } = action.payload;
      const targetId = galleryItem.networkItem.id;

      // Fixme: Don't combine the normalized item store with other properties...
      const existingItem = Normalized.getById<IGalleryItem, ItemId>(
        state.items,
        targetId,
      );

      state.items.byId = PojoMap.set(state.items.byId, targetId, {
        ...existingItem,
        galleryItemMetadata: {
          ...existingItem?.galleryItemMetadata,
          isPinned: true,
          pinnedTimestamp: moment().valueOf(),
        },
      });
      state.items.pinnedItemsOrderedIds = [
        cuid,
        ...state.items.pinnedItemsOrderedIds,
      ];
    },

    unpinItemAction: (
      state,
      action: PayloadAction<{
        cuid: Cuid;
        galleryItem: IGalleryItem;
        removeItem: boolean;
      }>,
    ) => {
      const { cuid, galleryItem, removeItem } = action.payload;

      /* If we're asked to unpin an item and remove it from the dashboard, remove it from our normalized list
       * of item ids and our items map. Otherwise, just remove the item's "pinned" status
       */
      if (removeItem) {
        state.items = {
          ...state.items,
          ...Normalized.removeItem(state.items, galleryItem.networkItem.id),
        };
        state.items.totalItemCount -= 1;
      } else {
        const item = Normalized.getById<IGalleryItem, ItemId>(
          state.items,
          galleryItem.networkItem.id,
        )!;
        item.galleryItemMetadata.isPinned = false;
        item.galleryItemMetadata.pinnedTimestamp = undefined;
      }

      state.items.pinnedItemsOrderedIds =
        state.items.pinnedItemsOrderedIds.filter(
          (pinnedCuid) => pinnedCuid !== cuid,
        );
    },

    movePinnedItemAction: (
      state,
      action: PayloadAction<{ cuid: Cuid; newPos: number; currentPos: number }>,
    ) => {
      const { currentPos, newPos, cuid } = action.payload;
      const newPinnedItems = [...state.items.pinnedItemsOrderedIds];
      newPinnedItems.splice(currentPos, 1);
      newPinnedItems.splice(newPos, 0, cuid);

      state.items.pinnedItemsOrderedIds = newPinnedItems;
    },

    requestPinnedMediaSucceeded: (
      state,
      action: PayloadAction<{
        pinnedMedia: IGalleryItem[];
        clientId: ClientId;
      }>,
    ) => {
      let itemsById = state.items.byId;
      const orderedIds: Cuid[] = [];

      for (const pinnedItem of action.payload.pinnedMedia) {
        if (!PojoMap.has(itemsById, pinnedItem.networkItem.id)) {
          itemsById = PojoMap.set(
            itemsById,
            pinnedItem.networkItem.id,
            pinnedItem,
          );
        }

        orderedIds.push(
          Cuid.buildFromNetworkItem(
            pinnedItem.networkItem,
            action.payload.clientId,
          ),
        );
      }

      state.items.byId = itemsById;
      state.items.pinnedItemsOrderedIds = orderedIds;
    },

    addCurrentlyVisibleToastType: (
      state,
      action: PayloadAction<GALLERIES_TOAST_TYPES>,
    ) => {
      state.currentlyVisibleToastTypes = PojoSet.add(
        state.currentlyVisibleToastTypes,
        action.payload,
      );
    },

    removeCurrentlyVisibleToastType: (
      state,
      action: PayloadAction<GALLERIES_TOAST_TYPES>,
    ) => {
      state.currentlyVisibleToastTypes = PojoSet.remove(
        state.currentlyVisibleToastTypes,
        action.payload,
      );
    },

    requestAutoApprovalConfigs: (state) => {
      state.flags.isFetchingAutoApprovalConfigs = true;
    },

    requestAutoApprovalConfigsSucceeded: (
      state,
      action: PayloadAction<IAutoApprovalConfiguration | undefined>,
    ) => {
      state.selectedReelAutoApprovalConfig = action.payload;
    },

    requestRightsNeededCounts: (state) => {
      state.flags.isFetchingRightsNeededCounts = true;
    },

    receiveRightsNeededCounts: (
      state,
      action: PayloadAction<Record<ReelId, number>>,
    ) => {
      state.rightsNeededItemCounts = action.payload;
      state.flags.isFetchingRightsNeededCounts = false;
    },

    setIsSkippingRights: (state, action: PayloadAction<PojoSet<ReelId>>) => {
      state.flags.isSkippingRightsOnItems = action.payload;
    },
  },
});

export const testReducers = caseReducers;

const {
  requestSiteData,

  setGalleryDataLoaded,
  setGalleryItemsLastFetch,
  requestGalleriesData,
  receiveGallery,
  receiveGalleriesDataSuccess,
  receiveSitesDataSuccess,
  requestFilteredGalleryItems,
  receiveFilteredGalleryItemsSucceeded,
  requestGalleryItems,
  resetGalleryItems,
  receiveGalleryItemsSucceeded,
  requestNextGalleryItems,
  receiveNextGalleryItemsSucceeded,
  removeGalleryItems,

  requestItemByCuid,
  receiveItemByCuidSucceeded,

  movePinnedItemAction,
  pinItemAction,
  unpinItemAction,
  requestPinnedMediaSucceeded,

  addCurrentlyVisibleToastType,
  removeCurrentlyVisibleToastType,

  requestAutoApprovalConfigs,
  requestAutoApprovalConfigsSucceeded,

  receiveRightsNeededCounts,

  setIsSkippingRights,
} = actions;

const getItems = async (
  dispatch: ReduxDispatch,
  getState: ReduxGetState,
  offset = 0,
) => {
  const REQUEST_ITEMS_LIMIT = 50;

  const selectedGalleryId = getState().galleries.picker.selectedGalleryId;
  const { searchFilterValue, sourceFilterValue, dateRange } =
    getState().galleries.actionBar;
  const isBazaarvoiceNetworkGallerySelected =
    getIsBazaarvoiceNetworkGallerySelected(getState());

  let items;
  let totalItemCount;

  // The 'allTime' date range is a special case. The API does not recognize 'allTime'
  // as a valid date range, so we pass `undefined` to fetch all items without a date filter.
  const dateRangeString = getStringFormatDateRangeValue(dateRange);
  let dateRangeValue =
    dateRangeString === 'allTime' ? undefined : dateRangeString;

  if (isBazaarvoiceNetworkGallerySelected) {
    // BV Syndication network "gallery"
    const clientId = ClientId.fromString(getState().session.client.id);

    const shareCompatibleSourceFilterValue =
      galleriesSourceFilterOptionToShareFilterOption(sourceFilterValue);

    const params: IGetBazaarvoiceNetworkSharedMediaRequestParams = {
      offset,
      limit: REQUEST_ITEMS_LIMIT,
      ...(searchFilterValue && { searchText: searchFilterValue }),
      ...(shareCompatibleSourceFilterValue && {
        moderationStatus: shareCompatibleSourceFilterValue,
      }),
    };

    const itemResult = await shareApi.getBazaarvoiceNetworkSharedMedia(
      clientId,
      params,
    );

    items = itemResult.items;
    totalItemCount = itemResult.totalItemCount;
  } else {
    // Standard gallery
    const itemResult = await galleriesApi.getGalleryItems(
      selectedGalleryId,
      offset,
      REQUEST_ITEMS_LIMIT,
      searchFilterValue,
      sourceFilterValue,
      dateRangeValue,
    );

    items = itemResult.galleryItems;
    totalItemCount = itemResult.totalCount;
  }

  if (items.length == 0) {
    dispatch(setGalleryItemsLastFetch());
  }

  return {
    items,
    totalItemCount,
  };
};

export const galleriesDataActions = {
  setGalleryDataLoaded,
  resetGalleryItems,
  removeGalleryItems,
  receiveGallery,
  setIsSkippingRights,

  getGalleries: createThunk(
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const isShareV2Vendor = getState().session.client.isShareV2Vendor;
      const isBazaarvoiceSyndicationEnabled =
        getIsBazaarvoiceNetworkSyndicationFrontendEnabled(getState());
      const isBazaarvoiceConnectClient =
        isShareV2Vendor && isBazaarvoiceSyndicationEnabled;

      if (!isBazaarvoiceConnectClient) {
        await dispatch(galleriesDataActions.getSites());

        dispatch(requestGalleriesData());
        const isCustomGalleriesClient =
          getGalleriesClientType(getState()) === GalleriesClientType.custom;

        const { galleries, siteName } = await (() => {
          if (isCustomGalleriesClient) {
            return galleriesApi.getCustomGalleries();
          } else {
            return galleriesApi.getGalleries();
          }
        })();

        dispatch(receiveGalleriesDataSuccess({ galleries, siteName }));

        return galleries;
      } else {
        return [];
      }
    },
  ),

  getSites: createThunk(() => async (dispatch: ReduxDispatch) => {
    dispatch(requestSiteData());

    const { sites } = await galleriesApi.getSites();

    dispatch(receiveSitesDataSuccess(sites));

    return sites;
  }),

  getFilteredGalleryItems: createThunk(
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const {
        flags: { isFetching, isFetchingNext, isFetchingFiltered },
      } = getState().galleries.data;

      if (isFetching || isFetchingNext || isFetchingFiltered) {
        return;
      }
      dispatch(requestFilteredGalleryItems());

      const { items, totalItemCount } = await getItems(dispatch, getState);

      dispatch(receiveFilteredGalleryItemsSucceeded({ items, totalItemCount }));
    },
  ),

  getGalleryItemByCuid: createThunk(
    (cuid: Cuid) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        if (getState().galleries.data.flags.isFetchingByCuid) {
          return;
        }

        dispatch(requestItemByCuid());

        const containerId = getState().galleries.picker.selectedGalleryId;
        const item = await galleriesApi.getGalleryItemByCuid(containerId, cuid);

        dispatch(receiveItemByCuidSucceeded(item));
      },
  ),

  getGalleryItems: createThunk(
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const {
        data: {
          flags: { isFetching, isFetchingNext, isFetchingFiltered },
        },
        picker: { selectedGalleryId },
      } = getState().galleries;

      if (isFetching || isFetchingNext || isFetchingFiltered) {
        return;
      }

      // getGalleryItems fetches items for the selected gallery, but sometimes there is no selected gallery
      if (selectedGalleryId) {
        dispatch(requestGalleryItems());

        const { items, totalItemCount } = await getItems(dispatch, getState);

        const isBazaarvoiceNetworkGallery =
          selectedGalleryId === FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID;

        if (!isBazaarvoiceNetworkGallery) {
          dispatch(galleriesDataActions.requestPinnedMedia());
        }

        dispatch(receiveGalleryItemsSucceeded({ items, totalItemCount }));
      }
    },
  ),

  getNextGalleryItems: createThunk(
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const {
        flags: { isFetching, isFetchingNext, isFetchingFiltered, isLastFetch },
      } = getState().galleries.data;

      if (isFetching || isFetchingNext || isFetchingFiltered || isLastFetch) {
        return;
      }

      dispatch(requestNextGalleryItems());

      const previouslyFetchedItems = getState().galleries.data.items.orderedIds;

      const { items } = await getItems(
        dispatch,
        getState,
        previouslyFetchedItems.length,
      );

      dispatch(receiveNextGalleryItemsSucceeded(items));
    },
  ),

  addItemsToSelectedGallery: createThunk(
    (cuids: PartialCuid[]) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        const galleriesState = getState().galleries;
        const { selectedGalleryId } = galleriesState.picker;
        const gallery = Normalized.getById(
          galleriesState.data.galleries,
          selectedGalleryId,
        );

        await bulkActionsApi.addToGalleries(cuids, [gallery.reelId]);
      },
  ),

  requestPinnedMedia: createThunk(
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const { galleries_pinned_media } = getState().session.features;
      if (!galleries_pinned_media) {
        return;
      }

      const { selectedGalleryId } = getState().galleries.picker;
      const clientId = ClientId.fromString(getState().session.client.id);

      const { galleryItems, totalCount } =
        await galleriesApi.getPinnedItems(selectedGalleryId);
      let pinnedMedia = galleryItems;

      while (pinnedMedia.length < totalCount) {
        const currentOffset = pinnedMedia.length;
        const { galleryItems: nextGalleryItems } =
          await galleriesApi.getPinnedItems(selectedGalleryId, currentOffset);

        pinnedMedia = pinnedMedia.concat(nextGalleryItems);
      }

      dispatch(requestPinnedMediaSucceeded({ pinnedMedia, clientId }));
    },
  ),

  movePinnedItem: createThunk(
    (itemId: ItemId, newPos: number, updateApi: boolean = false) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        const item = PojoMap.get(getState().galleries.data.items.byId, itemId);
        const { selectedGalleryId } = getState().galleries.picker;
        const clientId = ClientId.fromString(getState().session.client.id);
        const cuid = Cuid.buildFromNetworkItem(item.networkItem, clientId);
        const currentPos = PojoMap.get(
          getPinnedMediaPositionMap(getState()),
          cuid,
        );

        if (currentPos === newPos && !updateApi) {
          return;
        }

        dispatch(movePinnedItemAction({ cuid, newPos, currentPos }));

        if (updateApi) {
          try {
            await galleriesApi.updatePinnedMedia(
              selectedGalleryId,
              cuid,
              newPos,
            );
            dispatch(previewGalleryModalActions.updatePreview());
          } catch {
            const resultantPosition = PojoMap.get(
              getPinnedMediaPositionMap(getState()),
              cuid,
            );

            if (resultantPosition && currentPos) {
              dispatch(
                movePinnedItemAction({
                  cuid,
                  currentPos,
                  newPos: resultantPosition,
                }),
              );
            }
          }
        }
      },
  ),

  pinItem: createThunk(
    (itemId: ItemId) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        const galleryItem = PojoMap.get(
          getState().galleries.data.items.byId,
          itemId,
        );
        const clientId = ClientId.fromString(getState().session.client.id);
        const cuid = Cuid.buildFromNetworkItem(
          galleryItem.networkItem,
          clientId,
        );
        const galleriesClientType = getGalleriesClientType(getState());

        gaApi.recordEvent(
          gaApi.CATEGORY_GALLERIES,
          gaApi.ACTION_PINNED_MEDIA_PIN_ITEM,
          gaApi.LABEL_TOUCHPOINTS_GALLERIES,
        );

        const { selectedGalleryId } = getState().galleries.picker;
        dispatch(pinItemAction({ cuid, galleryItem }));

        dispatch(
          pinnedMediaToolbarActions.openToolbar(
            PinnedMediaToolbarContext.GALLERIES_DASHBOARD,
          ),
        );

        try {
          await galleriesApi.setPinnedMedia(selectedGalleryId, [cuid]);

          const pinnedMediaToastType = GALLERIES_TOAST_TYPES.PINNED_MEDIA;
          const currentlyVisibleToastTypes =
            getState().galleries.data.currentlyVisibleToastTypes;
          if (!currentlyVisibleToastTypes[pinnedMediaToastType]) {
            dispatch(addCurrentlyVisibleToastType(pinnedMediaToastType));
            const onHidden = () =>
              dispatch(removeCurrentlyVisibleToastType(pinnedMediaToastType));

            // Custom Galleries clients don't have gallery previews
            const extraData =
              galleriesClientType !== GalleriesClientType.custom
                ? {
                    linkText: 'Preview Gallery',
                    callback: () =>
                      dispatch(previewGalleryModalActions.openModal()),
                  }
                : undefined;

            dispatch(
              toastNotificationsActions.addToastNotification({
                title: 'Media Updated',
                message: `1 item pinned to the top of your Gallery! Rearrange pinned media by dragging and dropping in the
            Pinned Media section`,
                options: {
                  toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
                  onHidden,
                  extraData,
                },
              }),
            );
          }
        } catch {
          dispatch(unpinItemAction({ cuid, galleryItem, removeItem: false }));
        }
      },
  ),

  unpinItem: createThunk(
    (itemId: ItemId) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        const galleryItem = PojoMap.get(
          getState().galleries.data.items.byId,
          itemId,
        );
        const { selectedGalleryId } = getState().galleries.picker;
        const clientId = ClientId.fromString(getState().session.client.id);
        const cuid = Cuid.buildFromNetworkItem(
          galleryItem.networkItem,
          clientId,
        );
        const { sourceFilterValue } = getState().galleries.actionBar;

        gaApi.recordEvent(
          gaApi.CATEGORY_GALLERIES,
          gaApi.ACTION_PINNED_MEDIA_UNPIN_ITEM,
          gaApi.LABEL_TOUCHPOINTS_GALLERIES,
        );

        const oldPosition = PojoMap.get(
          getPinnedMediaPositionMap(getState()),
          cuid,
        );

        if (oldPosition !== undefined) {
          // Remove this item from our list of currently visible gallery items if we're using the
          // "pinned" filter
          dispatch(
            unpinItemAction({
              cuid,
              galleryItem,
              removeItem: sourceFilterValue === SOURCE_FILTER_VALUES.PINNED,
            }),
          );

          try {
            await galleriesApi.unpinItems(selectedGalleryId, [cuid]);
          } catch {
            dispatch(pinItemAction({ cuid, galleryItem }));

            // recently pinned items go to the end of the ordered ids array
            const currentPosition =
              getState().galleries.data.items.pinnedItemsOrderedIds.length - 1;
            dispatch(
              movePinnedItemAction({
                cuid,
                currentPos: oldPosition,
                newPos: currentPosition,
              }),
            );
          }
        }
      },
  ),

  processUiaCloseResponse: createThunk(
    (response: IUiaModalClosedResponse) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        const clientId = getState().session.client.id;
        const itemsById = getState().galleries.data.items.byId;
        const selectedGallery = getSelectedGallery(getState());
        const selectedAutoApprovalOption =
          getState().galleries.autoApprovalModal.selectedAutoApprovalStrategy;

        const cuid = Cuid.fromPartial(
          PartialCuid(response.universalItemId),
          ClientId.fromString(clientId),
        );

        const item = PojoMap.get(itemsById, response.itemId as ItemId);

        const isInstaShop =
          selectedGallery && isInstaShopGallery(selectedGallery);
        const hasProductizedModeEnabled =
          selectedAutoApprovalOption ===
          InstaShopAutoApprovalSources.productTaggedMedia;
        const wasPreviouslyPublished =
          item?.galleryItemMetadata.itemStatus ===
          GALLERY_ITEM_STATUS_VALUES.PUBLISHED;
        const isProductized = response.products.length > 0;

        const shouldApproveToInstaShop =
          isInstaShop &&
          hasProductizedModeEnabled &&
          !wasPreviouslyPublished &&
          isProductized;

        if (shouldApproveToInstaShop) {
          // If this is an instashop where only productized content should be approved
          // AND the item just went from 0 products to 1+ products, approve the item before refetching its status
          await dispatch(
            galleriesDataActions.addItemsToSelectedGallery([
              Cuid.toPartial(cuid),
            ]),
          );
        }

        dispatch(galleriesDataActions.getGalleryItemByCuid(cuid));
      },
  ),

  addToInstaShopGalleries: createThunk(
    (items: ISelectedItem[]) =>
      async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
        const isInstaShopClient =
          getGalleriesClientType(getState()) === GalleriesClientType.instashop;
        if (!isInstaShopClient) {
          crl8.logger?.cloud.error(
            'Non InstaShop client called addToInstaShopGallery',
          );
          return;
        }

        gaApi.recordEvent(
          gaApi.CATEGORY_GALLERIES,
          gaApi.ACTION_GALLERIES_ADD_ITEMS_TO_GALLERIES,
          gaApi.LABEL_TOUCHPOINTS_GALLERIES,
        );

        const partialCuids = items.map((item) => item.cuid);
        let addToGalleriesToast: IToastNotification;
        const instashopReelIds = getAllAlphabetizedGalleries(getState())
          .filter((gallery) => isInstaShopGallery(gallery))
          .map((gallery) => gallery.reelId);

        try {
          await bulkActionsApi.addToGalleries(partialCuids, instashopReelIds);

          const numItems = partialCuids.length;
          const itemsWord = numItems === 1 ? 'item' : 'items';

          const galleriesSuccessToast: IToastNotification = {
            title: 'Media Shared to InstaShop',
            message: `Successfully sent ${numItems} ${itemsWord} to InstaShop!`,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
              extraData: {
                linkText: 'Go To InstaShop',
                callback: () => navigateToState('instashop'),
              },
            },
          };

          addToGalleriesToast = galleriesSuccessToast;
        } catch {
          const galleriesFailureToast: IToastNotification = {
            title: 'Failed to Share to InstaShop',
            message: 'Failed to send items to InstaShop',
            type: ToastType.ERROR,
          };

          addToGalleriesToast = galleriesFailureToast;
        }

        dispatch(
          toastNotificationsActions.addToastNotification(addToGalleriesToast),
        );
      },
  ),

  requestAutoApprovalConfigurations: createThunk(
    (reelId: ReelId) => async (dispatch: ReduxDispatch) => {
      dispatch(requestAutoApprovalConfigs());

      const { data } = await galleriesApi.getAutoApprovalConfigs(reelId);
      if (data.length > 0) {
        const [config] = data;
        dispatch(requestAutoApprovalConfigsSucceeded(config));

        dispatch(
          autoApprovalModalActions.setAutoApprovalEnabled({
            isAutoApprovalEnabled: true,
          }),
        );
        dispatch(
          autoApprovalModalActions.setPreviousAutoApprovalStrategy({
            autoApprovalStrategy: config.autoApprovalStrategy,
          }),
        );
        dispatch(
          autoApprovalModalActions.setAutoApprovalStrategy({
            selectedAutoApprovalStrategy: config.autoApprovalStrategy,
          }),
        );
      } else {
        dispatch(autoApprovalModalActions.resetModal());
        dispatch(requestAutoApprovalConfigsSucceeded(undefined));
      }
    },
  ),

  getRightsNeededItemCounts: createThunk(() => async (dispatch, state) => {
    if (!state().galleries.data.flags.isFetchingRightsNeededCounts) {
      const needsRightsItemCounts = await galleriesApi.getNeedsRightsItems();
      dispatch(receiveRightsNeededCounts(needsRightsItemCounts));
    }
  }),
};

export default reducer;
